import warningIcon from "$assets/icons/warning-icon.svg?url";
import { Button } from "$components/button/button";
import { DialogBody } from "$components/dialog/dialog-body";
import { DialogContainer } from "$components/dialog/dialog-container";
import { DialogFooter } from "$components/dialog/dialog-footer";
import { DialogHeader } from "$components/dialog/dialog-header";
import { toast } from "$global-providers/toast-provider";
import { gql } from "@apollo/client";
import { Box, Grid, makeStyles, Typography } from "@material-ui/core";
import { FormattedMessage, useIntl } from "react-intl";
import { useDisconnectFromOrganisationPriceListMutation } from "./__generated__/disconnect-from-org-price-list-modal.gql";

const useStyles = makeStyles((theme) => ({
  warningIcon: {
    marginRight: theme.spacing(2),
  },
}));

gql`
  mutation disconnectFromOrganisationPriceList($priceListId: ID!) {
    priceListV2 {
      disconnectFromOrganisationPriceList(input: { priceListId: $priceListId }) {
        priceListId
      }
    }
  }
`;

export interface DisconnectFromOrgPriceListModalProps {
  priceListId: string;
  open: boolean;
  onClose: () => void;
  onCompleted?: () => void;
}

export const DisconnectFromOrgPriceListModal = ({
  priceListId,
  open,
  onClose,
  onCompleted,
}: DisconnectFromOrgPriceListModalProps) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const [disconnectFromOrgPriceList] =
    useDisconnectFromOrganisationPriceListMutation();

  const handleDisconnect = () => {
    disconnectFromOrgPriceList({ variables: { priceListId } })
      .then(() => {
        toast.success(
          formatMessage({
            id: "pricelist.disconnectFromPriceList.success",
            defaultMessage: "Disconnected from organisation price list",
          }),
        );
        onCompleted?.();
      })
      .catch((e) => {
        toast.error(e.message);
      });
    onClose();
  };

  return (
    <DialogContainer open={open}>
      <DialogHeader title="">
        <Box display="flex" alignItems="center">
          <img
            src={warningIcon}
            alt="warning"
            className={classes.warningIcon}
          />
          <Typography variant="h5">
            <FormattedMessage
              id="pricelist.modal.disconnectFromOrgPriceList.modal.title"
              defaultMessage="Disconnect from Org Price List"
            />
          </Typography>
        </Box>
      </DialogHeader>
      <DialogBody>
        <Grid container>
          <Typography variant="body1">
            <FormattedMessage
              id="pricelist.modal.disconnectFromOrgPriceList.description"
              defaultMessage="Once disconnected, prices on the organisation price list will not be automatically added to invoices. If you proceed,
              you will be able to rejoin at any time. Please confirm whether you would like to disconnect"
            />
          </Typography>
        </Grid>
      </DialogBody>
      <DialogFooter>
        <Button color="secondary" onClick={() => onClose()}>
          <FormattedMessage
            id="pricelist.modal.disconnectFromOrgPriceList.cancel"
            defaultMessage="Cancel"
          />
        </Button>
        <Button
          onClick={handleDisconnect}
          data-testid="pricelist.modal.disconnectFromOrgPriceList.accept"
        >
          <FormattedMessage
            id="pricelist.modal.disconnectFromOrgPriceList.confirm"
            defaultMessage="Disconnect"
          />
        </Button>
      </DialogFooter>
    </DialogContainer>
  );
};

export default DisconnectFromOrgPriceListModal;
